import { concatBytes, copyBytes } from "./bytes.js";
import { BlindBindError } from "./errors.js";

export class ByteStream {
  readonly #reader: ReadableStreamDefaultReader<Uint8Array>;
  #buffer = new Uint8Array(0);

  constructor(stream: ReadableStream<Uint8Array>) {
    this.#reader = stream.getReader();
  }

  async readExactly(length: number): Promise<Uint8Array> {
    if (!Number.isSafeInteger(length) || length < 0) throw new Error("Read length must be a non-negative integer");
    await this.#fill(length);
    const result = copyBytes(this.#buffer.subarray(0, length));
    this.#buffer = copyBytes(this.#buffer.subarray(length));
    return result;
  }

  async readUntil(delimiter: Uint8Array, maximumBytes: number): Promise<Uint8Array> {
    if (delimiter.byteLength === 0) throw new Error("Delimiter cannot be empty");
    for (;;) {
      const index = findBytes(this.#buffer, delimiter);
      if (index >= 0) {
        const end = index + delimiter.byteLength;
        const result = copyBytes(this.#buffer.subarray(0, end));
        this.#buffer = copyBytes(this.#buffer.subarray(end));
        return result;
      }
      if (this.#buffer.byteLength >= maximumBytes) {
        throw new BlindBindError("protocol_error", "Peer sent an oversized protocol header");
      }
      const result = await this.#reader.read();
      if (result.done) throw new BlindBindError("closed", "Peer closed before completing the protocol header");
      this.#buffer = concatBytes(this.#buffer, result.value);
    }
  }

  async readToEnd(maximumBytes: number): Promise<Uint8Array> {
    const chunks = this.#buffer.byteLength > 0 ? [this.#buffer] : [];
    let total = this.#buffer.byteLength;
    this.#buffer = new Uint8Array(0);
    for (;;) {
      const result = await this.#reader.read();
      if (result.done) return concatMany(chunks, total);
      total += result.value.byteLength;
      if (total > maximumBytes)
        throw new BlindBindError("protocol_error", "Peer response exceeds the configured maximum");
      chunks.push(copyBytes(result.value));
    }
  }

  release(): void {
    this.#reader.releaseLock();
  }

  async #fill(length: number): Promise<void> {
    while (this.#buffer.byteLength < length) {
      const result = await this.#reader.read();
      if (result.done) throw new BlindBindError("closed", "Peer closed during a framed message");
      this.#buffer = concatBytes(this.#buffer, result.value);
    }
  }
}

function findBytes(haystack: Uint8Array, needle: Uint8Array): number {
  const lastStart = haystack.byteLength - needle.byteLength;
  for (let start = 0; start <= lastStart; start += 1) {
    let matches = true;
    for (let offset = 0; offset < needle.byteLength; offset += 1) {
      if (haystack[start + offset] !== needle[offset]) {
        matches = false;
        break;
      }
    }
    if (matches) return start;
  }
  return -1;
}

function concatMany(chunks: Uint8Array[], total: number): Uint8Array {
  const result = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return result;
}
